import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import classNames from "classnames";
import { Modal } from "components";
import { Loading } from "components/panels";
import React, { useState } from "react";
import { formatShortDate } from "utilities";

interface Props {
  documentIndication: server.dto.DocumentIndication
  showId: number
}

const DocumentIndication = (props: Props) => {
  const { showId, documentIndication } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const infoApi = useQuery({
    queryFn: async () => {
      const response = await window.CoreApi.Document.ListDocumentsByShow({
        showId,
        documentType: documentIndication.documentType
      });
      return response.data?.data;
    },
    queryKey: ["documentIndication", showId, documentIndication.documentType],
    enabled: false,
    retry: false
  });

  const onIndicationClick = () => {
    infoApi.refetch();
    setIsModalOpen(true);
  };

  if (documentIndication.count === 0) {
    return <></>;
  }

  const getClasses = (doc: server.dto.DocumentForList) => {
    return classNames("s1_small", {
      "s1_tag darkgray-bg": doc.lifeCycle.value === "draft",
      "s1_tag purple-bg": doc.lifeCycle.value === "review",
      "s1_tag orange-bg": doc.lifeCycle.value === "pending" && !doc.overDue,
      "s1_tag red-bg": doc.lifeCycle.value === "pending" && doc.overDue,
      "s1_tag green-bg": doc.lifeCycle.value === "completed",
      "s1_tag gray-bg": doc.lifeCycle.value === "discarded"
    });
  };

  const dontDisplaySendDueDate = (doc: server.dto.DocumentForList) => {
    return doc.isV2 && (doc.lifeCycle.value !== "completed" && doc.lifeCycle.value !== "discarded" && doc.lifeCycle.value !== "pending");
  };

  return <>
    <div onClick={onIndicationClick} className="documentindication hoverable" style={{ backgroundColor: documentIndication.color }}>
      {documentIndication.documentCode}<sup>{documentIndication.count}</sup>
    </div>

    <Modal
      isOpen={isModalOpen}
      handleClose={() => { setIsModalOpen(false); }}
      title={site.currentEnvironment[documentIndication.documentType]?.plural}
      width="large"
    >
      {(infoApi.isLoading && infoApi.data === undefined) && <Loading />}
      <table className="s1_table list">
        <tbody>
          {infoApi.data?.map(item => (
            <tr key={item.documentId}>
              <td style={{ width: 30 }}>
                {item.isV2 && <i className="icon-rocket" title={RESX.ESign.eSign} />}
              </td>
              <td>
                <div className={classNames({ s1_gray: item.lifeCycle.value === "discarded" })}>
                  <a href={item.detailUrl}>{item.documentNumber}</a>
                </div>
                <div className={getClasses(item)}>
                  {item.lifeCycle.text}
                </div>
              </td>

              <td className={classNames({ s1_gray: item.lifeCycle.value === "discarded" })}>
                {item.addressee.name}
              </td>

              <td style={{ whiteSpace: "nowrap" }} className={classNames({ s1_gray: item.lifeCycle.value === "discarded" })}>
                {!dontDisplaySendDueDate(item) && <>
                  <div>
                    {RESX.DateTime.Send}: {formatShortDate(moment.utc(item.sendDate))}
                  </div>
                  <div className={classNames({
                    s1_red: item.overDue && item.lifeCycle.value !== "completed" && item.lifeCycle.value !== "discarded"
                  })}>
                    {RESX.DateTime.Due}: {formatShortDate(moment.utc(item.dueDate))}
                  </div>
                </>}
              </td>

              <td
                className={classNames("s1_cell_financialamount", { s1_gray: item.lifeCycle.value === "discarded" })}
                style={{ width: 75 }}>
                {item.sales?.amount !== 0 && <div>
                  {item.sales.formatted}
                </div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  </>;
};

export default DocumentIndication;